import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Checkbox,
  FormControlLabel,
  Chip,
} from "@mui/material";
import {
  ArrowBack as ArrowLeft,
  Download,
  Warning as AlertTriangle,
  CheckCircle,
  Cancel as XCircle,
} from "@mui/icons-material";
import { StepIndicator } from "./StepIndicator";
import CommonTable1 from "app/components/Tables/CommonTable1";
import * as XLSX from "xlsx";

export function ValidationScreen({
  schema,
  data,
  validationResults,
  onReupload,
  onProceed,
}) {
  const [showOnlyErrors, setShowOnlyErrors] = useState(false);

  const errorsByColumn = validationResults.errors.reduce((acc, error) => {
    error.errors.forEach((err) => {
      const columnName = err.split(" ").slice(1).join(" ");
      acc[columnName] = (acc[columnName] || 0) + 1;
    });
    return acc;
  }, {});

  const handleDownloadErrorReport = () => {
    const errorRows = validationResults.errors.map((error) => ({
      "Row Number": error.rowIndex + 2,
      Errors: error.errors.join(", "),
      ...error.data,
    }));

    const ws = XLSX.utils.json_to_sheet(errorRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Errors");
    XLSX.writeFile(wb, `${schema.name.replace(/\s+/g, "_")}_Errors.xlsx`);
  };

  const displayData = showOnlyErrors
    ? validationResults.allData.filter((row) => row._hasErrors)
    : validationResults.allData;

  const getRowErrors = (rowIndex) => {
    const error = validationResults.errors.find((e) => e.rowIndex === rowIndex);
    return error ? error.errors : [];
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#F9FAFB",
      }}
    >
      <Box
        sx={{
          maxWidth: "1280px",
          margin: "0 auto",
          padding: { xs: "32px 16px", sm: "32px 24px", lg: "32px 32px" },
        }}
      >
        <Button
          onClick={onReupload}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: "#6B7280",
            textTransform: "none",
            marginBottom: "24px",
            "&:hover": {
              color: "#111827",
              backgroundColor: "transparent",
            },
          }}
        >
          <ArrowLeft style={{ width: 20, height: 20 }} />
          Back to Upload
        </Button>

        <Box sx={{ mb: 4 }}>
          <StepIndicator currentStep={2} />
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            sx={{ color: "#111827", mb: 1, fontWeight: 600 }}
          >
            Data Validation: {schema.name}
          </Typography>
          <Typography sx={{ color: "#6B7280" }}>
            Review validation results and fix any errors before proceeding.
          </Typography>
        </Box>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} lg={4}>
            <Box
              sx={{
                backgroundColor: "white",
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                padding: "24px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: "#EFF6FF",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CheckCircle
                    style={{ width: 20, height: 20, color: "#3B82F6" }}
                  />
                </Box>
                <Box>
                  <Typography sx={{ color: "#6B7280", fontSize: "14px" }}>
                    Total Records
                  </Typography>
                  <Typography
                    sx={{ color: "#111827", fontSize: "24px", fontWeight: 600 }}
                  >
                    {validationResults.total}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Box
              sx={{
                backgroundColor: "white",
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                padding: "24px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: "#F0FDF4",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CheckCircle
                    style={{ width: 20, height: 20, color: "#16A34A" }}
                  />
                </Box>
                <Box>
                  <Typography sx={{ color: "#6B7280", fontSize: "14px" }}>
                    Valid Records
                  </Typography>
                  <Typography
                    sx={{ color: "#111827", fontSize: "24px", fontWeight: 600 }}
                  >
                    {validationResults.valid}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Box
              sx={{
                backgroundColor: "white",
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                padding: "24px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: "#FEF2F2",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <XCircle
                    style={{ width: 20, height: 20, color: "#DC2626" }}
                  />
                </Box>
                <Box>
                  <Typography sx={{ color: "#6B7280", fontSize: "14px" }}>
                    Invalid Records
                  </Typography>
                  <Typography
                    sx={{ color: "#111827", fontSize: "24px", fontWeight: 600 }}
                  >
                    {validationResults.invalid}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {validationResults.invalid > 0 && (
          <Box
            sx={{
              backgroundColor: "#FEF2F2",
              border: "1px solid #FECACA",
              borderRadius: "8px",
              padding: "24px",
              marginBottom: "32px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <AlertTriangle
                style={{
                  width: 20,
                  height: 20,
                  color: "#DC2626",
                  flexShrink: 0,
                  marginTop: 2,
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h6"
                  sx={{ color: "#7F1D1D", mb: 1, fontWeight: 600 }}
                >
                  Errors Found
                </Typography>
                <Typography sx={{ color: "#991B1B", fontSize: "14px", mb: 2 }}>
                  {validationResults.invalid} record(s) have validation errors.
                  Please fix these errors and re-upload, or proceed with valid
                  records only.
                </Typography>

                {Object.keys(errorsByColumn).length > 0 && (
                  <Box>
                    <Typography
                      sx={{ color: "#7F1D1D", fontSize: "14px", mb: 1 }}
                    >
                      Errors by field:
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {Object.entries(errorsByColumn).map(([column, count]) => (
                        <Chip
                          key={column}
                          label={`${column}: ${count}`}
                          sx={{
                            backgroundColor: "#FEE2E2",
                            color: "#991B1B",
                            fontSize: "14px",
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
              <Button
                onClick={handleDownloadErrorReport}
                variant="outlined"
                startIcon={<Download style={{ width: 16, height: 16 }} />}
                sx={{
                  px: 2,
                  py: 1,
                  textTransform: "none",
                  fontSize: "14px",
                  borderColor: "#FECACA",
                  color: "#B91C1C",
                  backgroundColor: "white",
                  "&:hover": {
                    backgroundColor: "#FEF2F2",
                    borderColor: "#FECACA",
                  },
                }}
              >
                Download Error Report
              </Button>
            </Box>
          </Box>
        )}

        <Box
          sx={{
            backgroundColor: "white",
            border: "1px solid #E5E7EB",
            borderRadius: "8px",
            overflow: "hidden",
            mb: 4,
          }}
        >
          <Box
            sx={{
              p: 2,
              borderBottom: "1px solid #E5E7EB",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6" sx={{ color: "#111827", fontWeight: 600 }}>
              Data Preview
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showOnlyErrors}
                  onChange={(e) => setShowOnlyErrors(e.target.checked)}
                  sx={{
                    "&.Mui-checked": {
                      color: "#3B82F6",
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: "14px", color: "#6B7280" }}>
                  Show only errors
                </Typography>
              }
            />
          </Box>

          <CommonTable1
            columns={[
              {
                header: "Sr.No",
                Cell: ({ cell }) => (
                  <Typography>
                    {parseInt(cell?.row?.id) + 1 < 10
                      ? `0${parseInt(cell?.row?.id) + 1}`
                      : parseInt(cell?.row?.id) + 1}
                  </Typography>
                ),
                size: 40,
              },
              ...schema.columns.map((column) => ({
                header: column.name,
                accessorKey: column.name,
                Cell: ({ cell }) => {
                  const rowIndex = cell?.row?.index;
                  const rowErrors = getRowErrors(rowIndex);
                  const hasError = rowErrors.some((error) =>
                    error.includes(column.name)
                  );
                  return (
                    <Box
                      sx={{
                        backgroundColor: hasError ? "#FEE2E2" : "transparent",
                        color: hasError ? "#991B1B" : "#0C2F49",
                        padding: "4px 8px",
                        borderRadius: "4px",
                      }}
                    >
                      <Typography>
                        {cell?.getValue() || "--"}
                      </Typography>
                    </Box>
                  );
                },
                maxSize: 150,
              })),
              {
                header: "Status",
                Cell: ({ cell }) => {
                  const rowIndex = cell?.row?.index;
                  const rowErrors = getRowErrors(rowIndex);
                  const hasError = rowErrors.length > 0;
                  return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {hasError ? (
                        <>
                          <XCircle
                            style={{ width: 16, height: 16, color: "#DC2626" }}
                          />
                          <Typography
                            sx={{ fontSize: "12px", color: "#DC2626" }}
                          >
                            Invalid
                          </Typography>
                        </>
                      ) : (
                        <>
                          <CheckCircle
                            style={{ width: 16, height: 16, color: "#16A34A" }}
                          />
                          <Typography
                            sx={{ fontSize: "12px", color: "#16A34A" }}
                          >
                            Valid
                          </Typography>
                        </>
                      )}
                    </Box>
                  );
                },
                // maxSize: 80,
              },
            ]}
            data={displayData}
            maxHeight="60vh"
            enableColumnActions={true}
            enableSorting={true}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
          <Button
            onClick={onReupload}
            variant="outlined"
            sx={{
              px: 3,
              py: 1.5,
              textTransform: "none",
              borderColor: "#D1D5DB",
              color: "#374151",
              "&:hover": {
                backgroundColor: "#F9FAFB",
                borderColor: "#D1D5DB",
              },
            }}
          >
            Re-upload File
          </Button>
          <Button
            onClick={onProceed}
            disabled={validationResults.valid === 0}
            variant="contained"
            sx={{
              px: 3,
              py: 1.5,
              textTransform: "none",
              backgroundColor: "#3B82F6",
              "&:hover": {
                backgroundColor: "#2563EB",
              },
              "&:disabled": {
                backgroundColor: "#D1D5DB",
              },
            }}
          >
            Proceed With Update ({validationResults.valid} records)
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
